import { Component, computed, input } from '@angular/core';
import {
  LucideArrowRight,
  LucideCalendarCheck,
  LucideDynamicIcon,
  LucideMessageCircleDashed,
  LucideMessageCircleHeart,
  LucideMessageCircleMore,
  LucidePhoneCall,
  LucidePhoneIncoming,
  LucideVideo,
  LucideVideoOff,
} from '@lucide/angular';
import { NgpFocusVisible, NgpHover } from 'ng-primitives/interactions';
import { StatusBadgeComponent } from '../../atoms/badges/status-badge/status-badge.component';
import { ButtonComponent } from '../../atoms/button/button.component';
import { AvatarComponent } from '../../molecules/avatar/avatar.component';
import { TextDirective } from '../../typography/text.directive';
import { StatusBadge, StatusCardState, StatusCardVariant } from './models/status-card';

export type { StatusBadge, StatusCardState, StatusCardVariant } from './models/status-card';

@Component({
  selector: 'joii-status-card',
  templateUrl: './status-card.component.html',
  styleUrl: './status-card.component.css',
  hostDirectives: [{ directive: NgpHover }, { directive: NgpFocusVisible }],
  host: {
    '[attr.tabindex]': 'state() === "unavailable" || disabled() ? -1 : 0',
    '[attr.data-variant]': 'variant()',
    '[attr.data-state]': 'state()',
    '[attr.data-full-width]': 'fullWidth()',
    '[attr.data-unavailable]': 'state() === "unavailable"',
    '[attr.data-disabled]': 'disabled()',
    '[attr.aria-disabled]': 'disabled()',
    '(click)': 'onHostClick($event)',
  },
  imports: [LucideDynamicIcon, TextDirective, StatusBadgeComponent, ButtonComponent, AvatarComponent],
})
export class StatusCardComponent {
  variant = input<StatusCardVariant>('chat');
  state = input<StatusCardState>('default');
  badge = input<StatusBadge | null>(null);
  fullWidth = input<boolean>(false);
  disabled = input<boolean>(false);
  body = input<string>('');
  avatars = input<string[]>([]);
  // Icons
  messageCircleHeart = LucideMessageCircleHeart;
  messageCircleDashed = LucideMessageCircleDashed;
  messageCircleMore = LucideMessageCircleMore;
  videoCallIcon = LucideVideo;
  videoCallBookedIcon = LucideCalendarCheck;
  videoCallUnavailableIcon = LucideVideoOff;
  videoCallJoinIcon = LucidePhoneIncoming;
  videoCallInProgressIcon = LucidePhoneCall;
  arrowRightIcon = LucideArrowRight;

  headerIcon = computed(() => {
    switch (this.variant()) {
      case 'chat':
        switch (this.state()) {
          case 'busy':
            return this.messageCircleDashed;
          case 'in-progress':
            return this.messageCircleMore;
          default:
            return this.messageCircleHeart;
        }
      case 'video':
        switch (this.state()) {
          case 'booked':
            return this.videoCallBookedIcon;
          case 'unavailable':
            return this.videoCallUnavailableIcon;
          case 'in-progress':
            return this.videoCallInProgressIcon;
          case 'join-video-call':
            return this.videoCallJoinIcon;
          default:
            return this.videoCallIcon;
        }
    }
    return undefined;
  });

  headerText = computed(() => {
    switch (this.variant()) {
      case 'chat':
        switch (this.state()) {
          case 'in-progress':
          case 'in-progress-triage':
            return 'Chat in progress';
          default:
            return 'Chat with a vet';
        }
      case 'video':
        switch (this.state()) {
          case 'in-progress':
            return 'Call in progress';
          case 'join-video-call':
            return 'Join video call';
          case 'booked':
            return 'Video call booked';
          default:
            return 'Video call a vet';
        }
      case 'whatsapp':
        return 'Whatsapp a vet';
    }
    return '';
  });

  badgeText = computed(() => {
    const badge = this.badge();
    if (!badge) return null;
    switch (badge.kind) {
      case 'reply-under':
        return `Replies in ≅ under ${badge.minutes}mins`;
      case 'busy':
        return `Busier than usual, replies in ≅ ${badge.minutes}mins`;
      case 'last-reply':
        return `Last reply ${badge.minutesAgo}mins ago`;
      case 'checking':
        return 'Checking availability...';
      case 'vets-available':
        return `${Number.isFinite(badge.count) ? badge.count : ''} vets available`.trim();
      case 'call-scheduled':
        return `A vet will call you between ${badge.startTime} - ${badge.endTime}`;
      case 'unavailable':
        return 'Currently unavailable';
      case 'calling':
        return 'A vet is calling you now';
      case 'call-ongoing':
        return `Call ongoing ${badge.duration}`;
      case 'custom':
        return badge.text;
      default:
        return '';
    }
  });

  badgeColor = computed(() => {
    const badge = this.badge();
    if (!badge) return null;
    switch (badge.kind) {
      case 'reply-under':
      case 'busy':
      case 'checking':
      case 'vets-available':
      case 'call-scheduled':
        return 'blue';
      case 'last-reply':
      case 'calling':
      case 'call-ongoing':
        return 'green';
      case 'unavailable':
        return 'red';
      case 'custom':
        return badge.color;
    }
    return null;
  });

  onHostClick(event: Event): void {
    if (this.state() === 'unavailable' || this.disabled()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
